import * as React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt } from '@fortawesome/pro-solid-svg-icons';

import TagChip from 'components/tags/tag_chip';

import TagsApi from './tags_api';
import TagsDropdown from './tags_dropdown';

import './tags_card.scss';
import { debounce, map } from 'lodash';

class TagsCard extends React.Component {
  static propTypes = {
    active: PropTypes.bool,
    editable: PropTypes.bool,
    filter: PropTypes.bool,
    paths: PropTypes.shape({
      tags: PropTypes.string
    }).isRequired,
    showLabel: PropTypes.bool,
    tags: PropTypes.arrayOf(PropTypes.string).isRequired,
    onAdd: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired
  };

  static defaultProps = {
    active: false,
    editable: true,
    filter: false,
    showLabel: true
  };

  state = {
    active: this.props.active || this.props.filter,
    firstLoad: false,
    searchText: '',
    suggestedTags: []
  };

  inputRef = React.createRef();
  wrapperRef = React.createRef();

  componentDidMount() {
    if (this.props.filter) return;

    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    if (this.props.filter) return;

    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (e) => {
    const { active } = this.state;

    if (
      active &&
      this.wrapperRef &&
      !this.wrapperRef.current.contains(e.target)
    ) {
      this.setState({
        active: false,
        suggestedTags: [],
        searchText: ''
      });
    }
  };

  handleSave = (tag) => {
    const { onAdd } = this.props;
    onAdd(tag);
    this.setState({ searchText: '', suggestedTags: [] });
    this.inputRef.current.focus();
  };

  handleEditTags = () => {
    this.setState({ active: true });
    this.handleUpdateSuggestedTags();
  };

  handleUpdateSearchText = (e) => {
    const { target: { value } } = e;
    this.setState({ searchText: value });
    this.handleUpdateSuggestedTags();
  };

  handleEnterKey = (e) => {
    const { suggestedTags, searchText } = this.state;
    const { editable } = this.props;

    if (e.key !== 'Enter') return;

    e.preventDefault();

    if (!editable && suggestedTags.length === 0) {
      return;
    } else if (suggestedTags.length > 0) {
      this.handleSave(suggestedTags[0]);
    } else if (searchText.length > 3) {
      this.handleSave(searchText);
    }
  }

  handleUpdateSuggestedTags = debounce(() => {
    const { editable, filter, paths, tags } = this.props;
    const { searchText } = this.state;
    const api = new TagsApi(paths);

    if (filter) {
      api.searchTags(searchText, tags).then((suggestedTags) => {
        if (searchText.length < 3) {
          this.setState({
            firstLoad: false,
            suggestedTags: []
          });
        } else {
          this.setState({
            firstLoad: true,
            suggestedTags
          });
        }
      });
    } else {
      api.searchTags(searchText).then((suggestedTags) => {
        this.setState({
          firstLoad: !editable && suggestedTags.length === 0,
          suggestedTags: suggestedTags.filter(tag => !tags.include(tag))
        });
      });
    }
  }, 200);

  renderTitle() {
    return (
      <div className="fw-bold pb-2">
        Tags
        {this.renderEditIcon()}
      </div>
    );
  }

  renderEditIcon() {
    if (this.state.active) return null;

    return (
      <FontAwesomeIcon
        className="ms-2 text-muted clickable"
        icon={faPencilAlt}
        size="sm"
        onClick={this.handleEditTags}
      />
    );
  }

  renderTagChips() {
    const { tags, onRemove } = this.props;
    const tagChips = map(tags, tag => (
      <TagChip
        key={tag}
        removable={true}
        tag={tag}
        onRemove={() => onRemove(tag)}
      />
    ));

    return (
      <div className="d-flex flex-wrap gap-1">
        {tagChips}
      </div>
    );
  }

  renderEmptyTags() {
    if (this.state.active) return null;

    return (
      <div className="text-muted">
        This is where you can add and remove tags
      </div>
    );
  }

  renderTags() {
    const { tags } = this.props;

    if (!tags) {
      return null;
    } else if (tags.length > 0) {
      return this.renderTagChips();
    } else {
      return this.renderEmptyTags();
    }
  }

  renderTagInput() {
    const { filter } = this.props;
    const { active, searchText } = this.state;
    if (!active) return null;

    return (
      <input
        autoComplete="off"
        autoFocus={!filter}
        className="w-100 form-control mt-1"
        ref={this.inputRef}
        value={searchText}
        onChange={this.handleUpdateSearchText}
        onKeyPress={this.handleEnterKey}
      />
    );
  }

  renderTagDropdown() {
    const { editable, filter } = this.props;
    const { active, firstLoad, searchText, suggestedTags } = this.state;
    if (!active) return null;

    return (
      <TagsDropdown
        currentValue={searchText}
        filter={filter || !editable}
        firstLoad={firstLoad}
        tags={suggestedTags}
        onAdd={this.handleSave}
      />
    );
  }

  render() {
    const { active } = this.state;

    return (
      <>
        {this.props.showLabel && this.renderTitle()}
        <div
          className={`tags-container bg-white p-0 position-relative ${active ? 'active' : ''}`}
          ref={this.wrapperRef}
        >
          {this.renderTags()}
          {this.renderTagInput()}
          {this.renderTagDropdown()}
        </div>
      </>
    );
  }
}

export default TagsCard;
